import { useEffect, useState } from "react";
import {
    fetchROAS,
    fetchTopCampaigns,
    type ROASResult,
    type Settings,
    type TopCampaignsResult,
} from "../api";
import { AdSpendRevenueChart } from "./AdSpendRevenueChart";
import { CampaignsTable } from "./CampaignsTable";
import { RoasChart } from "./RoasChart";
import { UnattributedOrdersModal } from "./UnattributedOrdersModal";

const fmt = (n: number, currency: string) =>
    n.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });

export function AdsView({
    settings,
    from,
    to,
}: {
    settings: Settings;
    from: string;
    to: string;
}) {
    const [roas, setRoas] = useState<ROASResult | null>(null);
    const [campaigns, setCampaigns] = useState<TopCampaignsResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErr(null);
        Promise.all([fetchROAS(settings, from, to), fetchTopCampaigns(settings, from, to, 20)])
            .then(([r, c]) => {
                if (cancelled) return;
                setRoas(r);
                setCampaigns(c);
            })
            .catch((e) => !cancelled && setErr((e as Error).message))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [settings, from, to, reloadKey]);

    if (err) {
        return (
            <div className="bg-red-950 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-6 text-sm">
                {err}
            </div>
        );
    }

    if (loading || !roas || !campaigns) {
        return <div className="text-okiru-muted">Loading…</div>;
    }

    // Empty-data state: no spend at all in this range.
    if (roas.blended.spend === 0 && roas.unattributed.revenue === 0) {
        return (
            <div className="text-okiru-muted bg-okiru-panel border border-okiru-muted/15 rounded-xl p-8 text-center">
                No ad activity in this range.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 h-[calc(100vh-12rem)] min-h-[600px]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <CompactMetric
                    label="Ad spend"
                    value={fmt(roas.blended.spend, roas.currency)}
                    sub={`${roas.blended.attributed_orders} attributed orders`}
                />
                <CompactMetric
                    label="Attributed revenue"
                    value={fmt(roas.blended.revenue, roas.currency)}
                    sub={`${roas.blended.attributed_orders} orders`}
                />
                <CompactMetric
                    label="Blended ROAS"
                    value={roas.blended.spend > 0 ? `${roas.blended.roas.toFixed(2)}×` : "—"}
                    sub={`${fmt(roas.blended.revenue, roas.currency)} ÷ ${fmt(roas.blended.spend, roas.currency)}`}
                />
                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="text-left hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-okiru-accent rounded-xl"
                    title="Click to manually attribute these orders"
                >
                    <CompactMetric
                        label="Unattributed revenue"
                        value={fmt(roas.unattributed.revenue, roas.currency)}
                        sub={`${roas.unattributed.orders} Meta-sourced orders without utm_id · click to attribute`}
                    />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
                <div className="flex flex-col gap-4 min-h-0">
                    <div className="flex-[3] min-h-0">
                        <AdSpendRevenueChart data={roas} />
                    </div>
                    <div className="flex-[2] min-h-0">
                        <RoasChart data={roas} />
                    </div>
                </div>
                <CampaignsTable
                    campaigns={campaigns}
                    unattributed={roas.unattributed}
                    currency={roas.currency}
                />
            </div>
            {modalOpen && (
                <UnattributedOrdersModal
                    settings={settings}
                    from={from}
                    to={to}
                    campaigns={campaigns.items.map((i) => ({ id: i.campaign_id, name: i.campaign_name }))}
                    onClose={() => setModalOpen(false)}
                    onSaved={() => setReloadKey((k) => k + 1)}
                />
            )}
        </div>
    );
}

function CompactMetric({ label, value, sub }: { label: string; value: string; sub?: string }) {
    return (
        <div className="bg-okiru-panel border border-okiru-muted/15 rounded-xl px-4 py-3">
            <div className="text-[11px] text-okiru-muted uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-semibold mt-0.5">{value}</div>
            {sub && <div className="text-xs text-okiru-muted mt-0.5 truncate">{sub}</div>}
        </div>
    );
}
